/**
 * Group A — the principal (`docs/api-v1/04-v1-surface.md` §3.1 A):
 *
 *     GET /v1/me                the bootstrap
 *     GET /v1/me/affiliate      this creator's own affiliate standing
 *     GET /v1/scraper-account   the Skool account Sideguide reads communities as
 *
 * All three are ACCOUNT-scoped — none of them takes a `skool_id` in its path — which
 * is why they share a module while every community-scoped group has its own. The
 * bootstrap is the reason the file exists and the rest of this docstring is about it;
 * the other two are at the bottom, each with the crossing from the legacy route it
 * replaces.
 *
 * `GET /v1/me` — the bootstrap. Doc 02 W1 + W2, doc 04 §4.1 + §4.2.
 *
 * WHAT THIS REPLACES. Every cold page load used to run
 *
 *     GET /admin/me  →  GET /admin/communities  →  the page's own fetch
 *
 * — two round trips before the page's first byte of real data was even REQUESTED,
 * on all twenty route files. Neither dependency was real: `/admin/me` returns only
 * what the bearer token already carried, and the selected community id is read out
 * of `localStorage` before any request goes out, so the client was waiting purely
 * to validate an id it already had. On top of that the sidebar fired four more
 * requests on every navigation, forever, to produce four integers.
 *
 * One request answers all of it: the user, the communities they hold, the selected
 * community RESOLVED SERVER-SIDE, the four badges as four `SELECT count(*)`, and
 * `server_time`.
 *
 * THE THREE DESIGN POINTS, and where each one lives:
 *
 *  1. `selected_skool_id` is resolved server-side. The client sends the id it has
 *     (`storedSkoolId()`); the server confirms it or returns the one it would have
 *     picked. A stale id is not an error here — see `selected_skool_id`'s note.
 *  2. THE PAGE ISSUES ITS OWN FETCH AT RTT 1, IN PARALLEL, against the stored id;
 *     if that id is stale the page fetch 403s and retries against the resolved one.
 *     That wrapper is `withCommunity` in `lib/community.svelte.ts` — the failure
 *     path costs 2 RTT, where today's SUCCESS path costs 3.
 *  3. `badges` are four counts over the same domain predicates the lists use, not
 *     four crippled `limit=1` list calls. `lib/todos.svelte.ts` consumes them.
 *
 * CACHED FOR THE SESSION. `ensureBootstrap()` issues at most one request per page
 * load however many callers ask for it, which is what takes doc 02 §6.2's five
 * unconditional per-navigation refetches to zero. Call `refreshBootstrap()` when a
 * write can have changed the account or the community list — a billing sync, the
 * paywall's "check again". Everything else should call `ensureBootstrap()`.
 *
 * WHO CALLS WHAT
 *   * a page          → `ensureBootstrap()` for `me`, in `Promise.all` with its own fetch
 *   * community state → `lib/community.svelte.ts` (the list + the selection)
 *   * the sidebar     → `lib/todos.svelte.ts` (the badges)
 * The last two coordinate through THIS module's cache rather than through each
 * other, so neither state module imports the other and there is still one request.
 */

import type { AccountPlanStatus, Bootstrap, MeCommunity } from '@api';

import { apiGet } from '@api/v1/http';
import type { AffiliateTotals, Badges, CommunityRef, QueryParams } from '@api';

import { identifyUser } from './analytics';
import { localDayStartISO, type AdminUser, type CommunityPlanStatus } from './admin';

export type { Badges };


export type { MeUser, OnboardingStage } from '@api';


// ---------------------------------------------------------------------------
// The stored selection
// ---------------------------------------------------------------------------
//
// The key lives here, at the bottom of the stack, because the bootstrap must read
// it BEFORE any request goes out — that is the whole of design point 1 — and it
// cannot import the state module that owns the selection without a cycle.
// `lib/community.svelte.ts` imports these two functions and is the only writer.

const SELECTED_KEY = 'sg_admin_skool';

/** The community id this browser last looked at, read straight out of
 *  `localStorage`. Null on a first visit. Never throws — a browser with storage
 *  disabled just gets the server's pick. */
export function storedSkoolId(): number | null {
  try {
    const raw = localStorage.getItem(SELECTED_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

export function storeSkoolId(id: number): void {
  try {
    localStorage.setItem(SELECTED_KEY, String(id));
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// The call, and the session cache
// ---------------------------------------------------------------------------

/**
 * One `/v1/me`, UNCACHED. Prefer `ensureBootstrap()`; this is for the badge-refresh
 * path, where the caller wants counts for a community that is not the cached
 * selection and must not disturb the cache.
 *
 * `since` is the start of the viewer's LOCAL day as a UTC instant: the inbox badge
 * counts TODAY's open items, and the server never guesses a zone (doc 04 §1.4).
 * Passing `skool_id: null` omits the parameter, and the response then carries no
 * `badges` at all.
 */
export function fetchBootstrap(skoolId: number | null): Promise<Bootstrap> {
  const params: QueryParams = {
    since: localDayStartISO(),
    ...(skoolId != null ? { skool_id: skoolId } : {}),
  };
  return apiGet<Bootstrap>('/me', params);
}

let cached: Bootstrap | null = null;
let inFlight: Promise<Bootstrap> | null = null;

async function load(skoolId: number | null): Promise<Bootstrap> {
  const body = await fetchBootstrap(skoolId);
  cached = body;
  // Re-identify on session resume / reload, exactly as `fetchMe()` did — except
  // once per page load instead of once per route mount.
  identifyUser(body.user);
  return body;
}

function start(skoolId: number | null): Promise<Bootstrap> {
  const p = load(skoolId).finally(() => {
    // Clear the in-flight slot either way: on success `cached` short-circuits the
    // next call, and on failure the next caller must be able to retry.
    if (inFlight === p) inFlight = null;
  });
  inFlight = p;
  return p;
}

/**
 * The bootstrap, at most once per page load. Safe to call from anywhere, as often
 * as you like: concurrent callers share one request and later callers get the
 * cached body without touching the network.
 */
export function ensureBootstrap(): Promise<Bootstrap> {
  if (cached) return Promise.resolve(cached);
  return inFlight ?? start(storedSkoolId());
}

/**
 * Force a fresh bootstrap, discarding the cache — for the writes that can change
 * the account itself: `POST /v1/me/billing/sync` on the Stripe return, the
 * paywall's "check again", a community added or removed.
 *
 * Pass a `skoolId` to compute the badges for a specific community; omit it to use
 * the stored selection.
 */
export function refreshBootstrap(skoolId?: number | null): Promise<Bootstrap> {
  cached = null;
  inFlight = null;
  return start(skoolId === undefined ? storedSkoolId() : skoolId);
}

/** The cached bootstrap if one has already resolved, else null. Never fetches —
 *  for the rare synchronous read; everything else should await `ensureBootstrap()`. */
export function cachedBootstrap(): Bootstrap | null {
  return cached;
}

/**
 * The bootstrap's user as the `AdminUser` the existing components take.
 *
 * `AdminShell`, `AdminSidebar`, `redirectIfAwaitingOnboarding` and every page's
 * `me` prop are typed `AdminUser`, and none of them changes shape in wave 6 —
 * so a page migrating to the bootstrap swaps two awaited calls for one:
 *
 *     me = await fetchMe();          //  ─┐ 2 RTT, then the page's own fetch
 *     await ensureCommunities();     //  ─┘
 *
 *     // becomes, with the page's own fetch running in the SAME round trip:
 *     const [boot, data] = await Promise.all([
 *       ensureBootstrap(),
 *       withCommunity((skoolId) => fetchWhatever(skoolId)),
 *     ]);
 *     me = asAdminUser(boot);
 *     if (redirectIfAwaitingOnboarding(me, navigate)) return;
 *
 * `skool_id` is the deprecated singular field; only `communityCount` reads it, and
 * the honest value for it now is the selected community.
 */
export function asAdminUser(boot: Bootstrap): AdminUser {
  return {
    email: boot.user.email,
    name: boot.user.name,
    role: boot.user.role,
    skool_ids: boot.user.skool_ids,
    pending_skool_url: boot.user.pending_skool_url,
    plan_status: boot.user.plan_status,
    trial_ends_at: boot.user.trial_ends_at,
    // Carried through because `redirectIfAwaitingOnboarding` needs it: an owner who
    // hasn't chosen a password may already HOLD a community (verification grants it
    // when the community was already ingested), so a community count can no longer
    // answer "is this account still mid-signup".
    onboarding_stage: boot.user.onboarding_stage,
  };
}

// ---------------------------------------------------------------------------
// `GET /v1/me/affiliate` — the creator's own affiliate standing
// ---------------------------------------------------------------------------

/** Program-wide constants, the single source of truth for the explainer copy.
 *  Identical to the legacy `AffiliateProgram`. */
export interface AffiliateProgram {
  default_rate_bps: number;
  hold_days: number;
  cookie_days: number;
}

/** A referred creator's community, with the live plan tier `PlanBadge` renders. The
 *  §2.3 `CommunityRef` plus `plan_status` — the legacy body omitted `slug`, which is
 *  the one field a link back to Skool would need. */
export interface ReferralCommunity extends CommunityRef {
  plan_status: CommunityPlanStatus;
}

/** One creator this affiliate brought in: when they arrived, what they hold, and the
 *  lifetime commission earned from them (every non-reversed row). */
export interface AffiliateReferral {
  user_id: number;
  name: string;
  /** UTC-aware ISO instant, or null. Parse with `parseTs` — the legacy route sent
   *  this through a bare `.isoformat()` on a naive column (doc 04 §1.5), so the date
   *  it rendered was shifted by the viewer's offset. */
  joined_at: string | null;
  earned_cents: number;
  communities: ReferralCommunity[];
}

/** One payout RUN. Every row a single `pay_out` claimed shares its `paid_out_at`
 *  stamp, so the history is grouped by it rather than listed per commission. */
export interface AffiliatePayout {
  paid_out_at: string | null;
  amount_cents: number;
}

/**
 * The creator-facing affiliate summary — everything that page renders.
 *
 * THE CROSSINGS FROM THE LEGACY `AffiliateSummary`:
 *   * the three balance buckets were already nested under `totals` and stay there —
 *     it is now literally the same `AffiliateTotals` the superadmin roster emits
 *     (`api/v1/shapes.py`), which is why the type is imported from `./admin` rather
 *     than declared twice;
 *   * `referred_count` and `paying_community_count` are new, and are the two numbers
 *     the legacy page derived in the browser (`referrals.length`, and a reduce over
 *     every referral's communities counting the `paid` ones). The server counts them
 *     over the rows it already has;
 *   * `referral.skools` is `referral.communities`, and each entry is a `CommunityRef`
 *     (`{id, slug, name}`) plus `plan_status` rather than `{id, name, plan_status}`;
 *   * `joined_at` / `paid_out_at` are UTC-aware instants, not the bare naive
 *     `.isoformat()` the legacy route emitted.
 *
 * WHAT USED TO BE MISSING, AND IS NOT ANY MORE. `referrals[]`, `payouts[]` and
 * `paying_community_count` shipped absent while the assembly lived inside
 * `api/affiliates.py`, a router that a v1 route may neither import from nor copy.
 * `api/v1/principal.py` calls `domain.affiliates.creator_summary` now, so the whole
 * page is on this route. Migrating it also FIXES A LIVE 500: the legacy handler
 * rebinds the imported `holder_flags` function to a dict and then calls it
 * (`api/affiliates.py:151,159`), so `GET /admin/affiliate/summary` raises `TypeError`
 * for any affiliate who has at least one referral — i.e. for every affiliate the page
 * is actually for.
 */
export interface MyAffiliate {
  is_affiliate: boolean;
  /** The referral code; null for a non-affiliate, who still gets the program
   *  defaults and zero totals so the page can render its explainer. */
  code: string | null;
  /** The rate this account earns; for a non-affiliate, the program default. */
  rate_bps: number;
  disabled: boolean;
  referred_count: number;
  /** Referred communities on a PAYING tier — `paid` only; a trial or a free
   *  community has not converted yet (`domain.affiliates.PAYING_PLAN_STATUSES`). */
  paying_community_count: number;
  program: AffiliateProgram;
  totals: AffiliateTotals;
  referrals: AffiliateReferral[];
  /** Most recent run first; empty until the first manual payout. */
  payouts: AffiliatePayout[];
}

/** `GET /v1/me/affiliate`. Account-scoped and gated `admin`, not `active`: an
 *  EXPIRED creator is still an affiliate and must be able to see their link and their
 *  earnings. Issue it in PARALLEL with `ensureBootstrap()`, never after it — the
 *  `await` it sits behind today was for the page shell, not for the data (W11). */
export function fetchMyAffiliate(): Promise<MyAffiliate> {
  return apiGet<MyAffiliate>('/me/affiliate');
}

// ---------------------------------------------------------------------------
// `GET /v1/scraper-account` — who to promote
// ---------------------------------------------------------------------------

/**
 * The Skool account Sideguide reads communities as.
 *
 * SHAPE-IDENTICAL to the legacy `RosterAccount` — same three keys, same nullability
 * — so this is the one migration in the set where only the path and the error
 * envelope change. `handle`/`name` are null until our account has been seen in some
 * community, and the prompt then falls back to naming the email alone.
 *
 * Not community-scoped: it is one global fact, so the onboarding wizard and the
 * roster-access banner name the same account without either passing an id. The gate
 * is `admin`, not `active`, deliberately — this is part of the PRE-payment onboarding
 * prompt, so a creator who has not paid must be able to read it.
 */
export interface ScraperAccount {
  email: string;
  handle: string | null;
  name: string | null;
}

export function fetchScraperAccount(): Promise<ScraperAccount> {
  return apiGet<ScraperAccount>('/scraper-account');
}


// The `/v1/me` shapes live in `@api/v1/me` — they are what the route returns. This
// module keeps the session LOGIC built on them: the cache, the redirects, analytics.
export type { AccountPlanStatus, Bootstrap, MeCommunity };